<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"
import type { BaseKpiPassThrough } from "@core/components/base/kpi/baseKpiPassThrough"
import { createUniqueId } from "@core/helpers"
import type { DimensionnementPvAnalysis } from "@modules/pv/types/dimensionnement-pv.types"
import type { PvChartsMap } from "@modules/pv/types/pv-common.types"
import {
    buildPvDimensionnementPlotlyGraph,
    buildPvDimensionnementSeasonHourlyWeekLineGraph,
    extractPvDimensionnementMainChartLegendKpiItems,
} from "@modules/pv/utils/buildPvDimensionnementPlotlyGraph"
import { useChartExport } from "@modules/pv/composables/useChartExport"

// ================================
// TYPES
// ================================
type PvDimFrequency = "hour" | "day" | "week" | "month"

type PvDimSeason = "saison_basse" | "saison_haute"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    analyse: DimensionnementPvAnalysis
    /** Charts fusionnés analyse + gabarit (clés `dimensionnement_pv_*`). */
    mergedCharts: PvChartsMap
    /** Indique qu’une mise à jour / relance est en cours (désactive certains boutons). */
    busy?: boolean
    /** Suppression en cours (désactive la corbeille). */
    isDeleting?: boolean
}>()

const emit = defineEmits<{
    (event: "click:delete", analyse: DimensionnementPvAnalysis): void
    (event: "click:launch-etude-eco", analyse: DimensionnementPvAnalysis): void
    (event: "click:modify", analyse: DimensionnementPvAnalysis): void
    (event: "click:download-data", analyse: DimensionnementPvAnalysis): void
    (event: "click:download-report", analyse: DimensionnementPvAnalysis): void
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            scenarioTitle: "Consommation, production et autoconsommation (kWh)",
            createdAt: "Réalisée le {date}",
            freqHour: "Heure",
            freqDay: "Jour",
            freqWeek: "Semaine",
            freqMonth: "Mois",
            legendConsommation: "Consommation",
            legendProduction: "Production PV",
            legendAutoconso: "Autoconsommation",
            legendSurplus: "Surplus injecté",
            /** Profil saison (légendes = legacy `component.pv_weekly.chart`) */
            seasonLegendWeeklyConso: "Consommation Hebdomadaire",
            seasonLegendTotalInstall: "Installation totale",
            seasonLegendSegmentPrefix: "Pan de toit",
            kpiAutoconso: "Taux d'autoconsommation",
            kpiAutoprod: "Taux d'autoproduction",
            kpiCouverture: "Taux de couverture",
            kpiInfoAutoconso:
                "Il s’agit du ratio entre l’énergie auto-consommée et l’énergie produite",
            kpiInfoAutoprod:
                "Il s’agit du ratio entre l’énergie autoconsommée et l’énergie consommée",
            kpiInfoCouverture:
                "Il s’agit du ratio entre l’énergie produite et l’énergie consommée",
            weeklyTitle: "Profil de production par saison",
            seasonLow: "Saison basse • D’avril jusqu’à octobre",
            seasonHigh: "Saison haute • De novembre jusqu’à mars",
            noHourlyProfile:
                "Le profil horaire détaillé n’est pas disponible pour cette étude.",
            noChartData:
                "Aucune série graphique exploitable pour cette fréquence. Les calculs sont peut‑être encore en cours ou en erreur.",
            noDataHourly:
                "Les données horaires complètes ne sont pas disponibles à partir du {date}. Les graphiques utilisent les agrégats disponibles.",
            missingHourlyDays:
                "L'analyse a été réalisée sur une période de {nb} jours / 365, nous ne disposons pas de suffisamment de données horaires pour les autres jours de l'année.",
            hourlyDataHint:
                "Les métriques de consommation et de production sont calculées sur la période visualisée, tandis que les métriques d'autoconsommation et de surplus injecté ne sont calculées que sur la période où le compteur remonte des données horaires.",
            launchEco: "Lancer une étude économique",
            deleteTooltip: "Supprimer cette simulation",
            actionMenuAria: "Autres actions",
            modifyParams: "Modifier les paramètres",
            exportData: "Exporter les données",
            downloadReport: "Télécharger le rapport",
            exportExcelMain: "Télécharger Excel",
            exportExcelWeekly: "Télécharger Excel",
        },
    },
})

// ================================
// ÉTAT LOCAL
// ================================
const frequency = ref<PvDimFrequency>("day")
const season = ref<PvDimSeason>("saison_basse")
const plotlyNonce = ref(createUniqueId())
const scenarioMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)
const { downloadChartAsExcel } = useChartExport()

// ================================
// DONNÉES CALCULÉES
// ================================
const getFrequencyOptions = computed(() => [
    { label: t("freqHour"), value: "hour" as const },
    { label: t("freqDay"), value: "day" as const },
    { label: t("freqWeek"), value: "week" as const },
    { label: t("freqMonth"), value: "month" as const },
])

const getSeasonOptions = computed(() => [
    { label: t("seasonLow"), value: "saison_basse" as const },
    { label: t("seasonHigh"), value: "saison_haute" as const },
])

const getFrequencyChartKey = computed(() => {
    const map: Record<PvDimFrequency, string> = {
        hour: "dimensionnement_pv_hourly",
        day: "dimensionnement_pv_daily",
        week: "dimensionnement_pv_weekly",
        month: "dimensionnement_pv_monthly",
    }
    return map[frequency.value]
})

const getChartLabels = computed(() => ({
    consommation: t("legendConsommation"),
    production: t("legendProduction"),
    autoconsommation: t("legendAutoconso"),
    surplus: t("legendSurplus"),
}))

const getSeasonProfileLineLabels = computed(() => ({
    weeklyConsumption: t("seasonLegendWeeklyConso"),
    totalInstallation: t("seasonLegendTotalInstall"),
    segmentPrefix: t("seasonLegendSegmentPrefix"),
}))

const getSeasonSegmentIds = computed(
    () => props.analyse.inputs?.list_params_pv?.map((s) => s.id) ?? []
)

const getMainChartBlock = computed(
    () => props.mergedCharts[getFrequencyChartKey.value]
)

const getMainPlotly = computed(() =>
    buildPvDimensionnementPlotlyGraph(
        getMainChartBlock.value,
        getChartLabels.value
    )
)

/** Totaux par tracé (kWh) pour la fréquence courante — légende KPI au-dessus du sélecteur. */
const getMainChartLegendKpiItems = computed(() =>
    extractPvDimensionnementMainChartLegendKpiItems(
        getMainChartBlock.value,
        getChartLabels.value
    )
)

const getHourlySeasonKey = computed(
    () => `dimensionnement_pv_hourly_${season.value}`
)

const getWeeklyPlotly = computed(() =>
    buildPvDimensionnementSeasonHourlyWeekLineGraph(
        props.mergedCharts[getHourlySeasonKey.value],
        {
            labels: getSeasonProfileLineLabels.value,
            segmentIds: getSeasonSegmentIds.value,
        }
    )
)

const getCreatedAtLabel = computed(() => {
    const raw = props.analyse.createdAt
    if (!raw) return ""
    return t("createdAt", { date: moment(raw).format("DD/MM/YYYY") })
})

const getDisplayName = computed(() => {
    const n = props.analyse.name_analyse
    return typeof n === "string" && n.trim().length > 0 ? n : "—"
})

const getScenarioMenuItems = computed(() => [
    {
        label: t("modifyParams"),
        icon: "pi pi-pencil",
        command: () => {
            emit("click:modify", props.analyse)
        },
    },
    {
        label: t("exportData"),
        icon: "pi pi-file-export",
        command: () => {
            emit("click:download-data", props.analyse)
        },
    },
    {
        label: t("downloadReport"),
        icon: "pi pi-download",
        command: () => {
            emit("click:download-report", props.analyse)
        },
    },
])

function getDimRatioKpiPt(): BaseKpiPassThrough {
    return {
        root: "!max-w-none w-full min-h-0 h-auto min-w-0 rounded-md border border-surface-200 p-4 gap-3 bg-white shadow-none justify-start items-stretch mx-0 sm:max-w-none h-full",
        title: "normal-case text-xs font-medium text-primary-600 flex flex-wrap items-center justify-center gap-1 text-center",
        valueRow:
            "flex flex-col w-full min-w-0 flex-1 justify-start items-center text-center gap-1 mt-1",
        value: "text-lg font-semibold text-primary-900 tabular-nums",
    }
}

const getRatioKpiItems = computed(() => {
    const d = props.analyse.data
    if (!d) return []
    const fmt = (r: { str_value?: string | null } | undefined) => {
        const s = r?.str_value
        if (typeof s === "string" && s.trim().length > 0) return `${s.trim()} %`
        return "—"
    }
    return [
        {
            type: "ratio_autoconso",
            label: t("kpiAutoconso"),
            value: fmt(d.ratio_autoconso),
            info: t("kpiInfoAutoconso"),
        },
        {
            type: "ratio_autoprod",
            label: t("kpiAutoprod"),
            value: fmt(d.ratio_autoprod),
            info: t("kpiInfoAutoprod"),
        },
        {
            type: "ratio_couverture",
            label: t("kpiCouverture"),
            value: fmt(d.ratio_couverture),
            info: t("kpiInfoCouverture"),
        },
    ]
})

const getShowHourlyDataInfo = computed(() => {
    const d = props.analyse.data
    if (!d?.has_all_data_hourly?.value) {
        const first = d?.first_data_hourly?.value
        const dateStr =
            typeof first === "string" && first.length > 0
                ? moment(first).format("DD/MM/YYYY")
                : "—"
        return { kind: "no_hourly" as const, dateStr }
    }
    const nb = d.nb_days_full_hourly?.value
    if (typeof nb === "number" && nb !== 365) {
        return { kind: "partial" as const, nb }
    }
    return null
})

// ================================
// SYNCHRONISATION
// ================================
watch(
    () => props.analyse._id,
    () => {
        plotlyNonce.value = createUniqueId()
    }
)

// ================================
// INTERACTIONS
// ================================
async function onOpenScenarioMenu(event: Event): Promise<void> {
    await nextTick()
    scenarioMenuRef.value?.toggle(event)
}

function onClickDelete(): void {
    emit("click:delete", props.analyse)
}

function onClickLaunchEco(): void {
    emit("click:launch-etude-eco", props.analyse)
}

function onClickDownloadExcelMain(): void {
    downloadChartAsExcel(getMainPlotly.value, {
        chartSlug: `dimensionnement-scenario-${frequency.value}`,
        buildingSlug: props.analyse.name_analyse ?? props.analyse._id,
        xAxisLabel: t("scenarioTitle"),
        sheetName: "Scénario",
    })
}

function onClickDownloadExcelWeekly(): void {
    downloadChartAsExcel(getWeeklyPlotly.value, {
        chartSlug: `dimensionnement-profil-hebdo-${season.value}`,
        buildingSlug: props.analyse.name_analyse ?? props.analyse._id,
        xAxisLabel: "Heure",
        sheetName: "Profil hebdo",
    })
}
</script>

<template>
    <BaseTitledCard
        class="w-full overflow-hidden border border-surface-200 bg-surface-0 shadow-sm"
        content-class="!p-0"
        header-class="!text-left"
    >
        <template #header>
            <div
                class="flex w-full flex-col gap-3 font-semibold md:flex-row md:items-center md:justify-between"
            >
                <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <span class="text-lg font-semibold text-primary-800">
                        {{ getDisplayName }}
                    </span>
                    <span
                        v-if="getCreatedAtLabel"
                        class="text-sm font-normal text-primary-800/90"
                    >
                        {{ getCreatedAtLabel }}
                    </span>
                </div>
                <div
                    class="flex shrink-0 flex-wrap items-center justify-end gap-2"
                >
                    <BaseButton
                        v-tooltip.top="t('actionMenuAria')"
                        icon="pi pi-ellipsis-h"
                        size="small"
                        severity="secondary"
                        text
                        rounded
                        :disabled="Boolean(busy) || Boolean(isDeleting)"
                        :aria-label="t('actionMenuAria')"
                        @click="onOpenScenarioMenu($event)"
                    />
                    <BaseMenu
                        ref="scenarioMenuRef"
                        :model="getScenarioMenuItems"
                        popup
                    />
                    <BaseButton
                        v-tooltip.top="t('deleteTooltip')"
                        icon="pi pi-trash"
                        size="small"
                        text
                        rounded
                        severity="danger"
                        :loading="Boolean(isDeleting)"
                        :disabled="Boolean(busy) || Boolean(isDeleting)"
                        :aria-label="t('deleteTooltip')"
                        @click="onClickDelete"
                    />
                    <BaseButton
                        type="button"
                        icon="pi pi-chart-line"
                        icon-pos="right"
                        size="small"
                        :label="t('launchEco')"
                        :disabled="Boolean(busy) || Boolean(isDeleting)"
                        @click="onClickLaunchEco"
                    />
                </div>
            </div>
        </template>
        <template #content>
            <div
                class="flex flex-col gap-4 bg-[var(--p-content-background)] px-4 py-5 sm:px-8 sm:py-6"
            >
                <div
                    v-if="getRatioKpiItems.length > 0"
                    class="grid grid-cols-1 gap-2 sm:grid-cols-3"
                >
                    <div
                        v-for="kpi in getRatioKpiItems"
                        :key="kpi.type"
                        class="min-w-0"
                    >
                        <BaseKPI
                            :type="kpi.type"
                            :label="kpi.label"
                            :value="kpi.value"
                            :clickable="false"
                            :pt="getDimRatioKpiPt()"
                        >
                            <template #subtitle>
                                <div
                                    class="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-center"
                                >
                                    <BaseTooltipMessage
                                        :content="kpi.info"
                                        position="top"
                                        size="small"
                                        severity="info"
                                    />
                                </div>
                            </template>
                        </BaseKPI>
                    </div>
                </div>

                <div v-if="getShowHourlyDataInfo" class="flex flex-col gap-2">
                    <BaseMessage
                        v-if="getShowHourlyDataInfo.kind === 'no_hourly'"
                        severity="info"
                        :closable="false"
                    >
                        {{
                            t("noDataHourly", {
                                date: getShowHourlyDataInfo.dateStr,
                            })
                        }}
                    </BaseMessage>
                    <template v-else>
                        <BaseMessage
                            severity="info"
                            :closable="false"
                            size="small"
                        >
                            {{
                                t("missingHourlyDays", {
                                    nb: getShowHourlyDataInfo.nb ?? 0,
                                })
                            }}
                        </BaseMessage>
                        <BaseMessage
                            severity="info"
                            :closable="false"
                            size="small"
                        >
                            {{ t("hourlyDataHint") }}
                        </BaseMessage>
                    </template>
                </div>

                <div class="flex flex-col gap-6">
                    <div
                        class="flex flex-wrap items-center justify-between gap-2"
                    >
                        <h3 class="text-base font-semibold text-primary">
                            {{ t("scenarioTitle") }}
                        </h3>
                        <BaseButton
                            :label="t('exportExcelMain')"
                            icon="pi pi-file-excel"
                            severity="secondary"
                            text
                            size="small"
                            :disabled="!getMainPlotly"
                            @click="onClickDownloadExcelMain"
                        />
                    </div>
                    <DataChartLegendKPI :items="getMainChartLegendKpiItems" />
                    <BaseSelectButton
                        v-model="frequency"
                        :options="getFrequencyOptions"
                        option-label="label"
                        option-value="value"
                        :allow-empty="false"
                    />
                    <div
                        v-if="getMainPlotly"
                        class="min-h-[22rem] w-full overflow-hidden rounded-lg border border-surface-200 bg-white"
                        style="height: 24rem"
                    >
                        <PlotlyChart
                            :key="`${plotlyNonce}-main-${frequency}`"
                            :graph="getMainPlotly"
                        />
                    </div>
                    <BaseMessage v-else severity="warn" :closable="false">
                        {{ t("noChartData") }}
                    </BaseMessage>
                </div>

                <BaseDivider />

                <div class="flex flex-col gap-6">
                    <div
                        class="flex flex-wrap items-center justify-between gap-2"
                    >
                        <h3 class="text-base font-semibold text-primary">
                            {{ t("weeklyTitle") }}
                        </h3>
                        <BaseButton
                            :label="t('exportExcelWeekly')"
                            icon="pi pi-file-excel"
                            severity="secondary"
                            text
                            size="small"
                            :disabled="!getWeeklyPlotly"
                            @click="onClickDownloadExcelWeekly"
                        />
                    </div>
                    <BaseSelectButton
                        v-model="season"
                        :options="getSeasonOptions"
                        option-label="label"
                        option-value="value"
                        :allow-empty="false"
                    />
                    <div
                        v-if="getWeeklyPlotly"
                        class="min-h-[22rem] w-full overflow-hidden rounded-lg border border-surface-200 bg-white"
                        style="height: 24rem"
                    >
                        <PlotlyChart
                            :key="`${plotlyNonce}-weekly-${season}`"
                            :graph="getWeeklyPlotly"
                        />
                    </div>
                    <BaseMessage v-else severity="info" :closable="false">
                        {{ t("noHourlyProfile") }}
                    </BaseMessage>
                </div>
            </div>
        </template>
    </BaseTitledCard>
</template>
